<template>
  <div class="kpi-benchmark-editor">
    <div class="editor-header">
      <div class="widget-type">
        <img
          :src="moduleConfigs.icon"
          alt="" />
        <div>Benchmark</div>
      </div>
      <Tabs
        v-model="inputSectionTab"
        :tabs="tabs"
        :is-objects="true"
        @input="scrollToSection" />
    </div>

    <div
      class="editor"
      @scroll="throttleScroll">
      <div
        id="display-label"
        class="editor-section">
        <h2>Display label</h2>
        <div class="body">
          <b-field
            label="Display Label"
            class="input-field">
            <MeasureLabelInput
              v-model="inputs.name"
              placeholder="e.g. LY" />
          </b-field>
          <p class="section-help">
            Drawn once per KPI measure, above whichever lines this benchmark
            shows. Always drawn — a figure the reader cannot name is not worth
            the room.
          </p>
        </div>
      </div>

      <div
        id="measure"
        class="editor-section">
        <h2>Measure</h2>
        <div class="body">
          <MeasureConfigEditor
            v-model="inputs.measure_config"
            :errors="childErrors('measure_config', errors)"
            :context="{
              reportDimensions: [],
              defaultMeasureOption: inheritMeasureOption,
              tableDefaultTimeRange: context.moduleDefaultTimeRange,
              timeRangeDefaultLabel: 'Use the KPI measure default',
              timeRangeDefaultName: context.timeRangeDefaultName,
            }" />
          <p class="section-help">
            What each card is measured against — usually just a time offset
            with the measure left inherited. Both lines come from it: the value
            line reads it, and the change compares the card's own measure to
            it.
          </p>

          <MeasureMappingSelect
            v-model="inputs.measure_config"
            :kpi-measures="context.kpiMeasures || []"
            help="Reads the benchmark through a mapping of the KPI's own
              measure — a budget or forecast, with each card resolving its own.
              Use this instead of naming a measure above, which would mean the
              same one on every card." />
        </div>
      </div>

      <div
        id="include"
        class="editor-section">
        <h2>Include</h2>
        <div class="body">
          <b-field
            class="input-field"
            label="Include:">
            <singleselect
              v-model="inputs.show"
              :append-to-body="true"
              :options="showOptions"
              :return-as-object="false"
              :enable-search="false"
              label="label"
              track-by="key"
              header-placeholder="Select what to include" />
          </b-field>
          <p class="section-help">
            {{ showHelp }}
          </p>
        </div>
      </div>

      <div
        v-if="showsChange"
        id="variance"
        class="editor-section">
        <h2>Variance</h2>
        <div class="body">
          <b-field
            class="input-field"
            label="Display the change as:">
            <singleselect
              v-model="inputs.difference_type"
              :append-to-body="true"
              :options="differenceTypeOptions"
              :return-as-object="false"
              :enable-search="false"
              label="label"
              track-by="key"
              header-placeholder="Select difference type" />
          </b-field>

          <ConditionalFormatEditor
            v-model="inputs.conditional_format"
            :difference-type="inputs.difference_type"
            :hide-direction="hasMeasureDirection"
            :inherited-comparison-help="inheritedComparisonHelp"
            :context="{ parentMeasure: null }">
            <template #direction>
              <b-field class="input-field">
                <b-switch
                  v-model="inputs.invert_direction"
                  type="is-info">
                  Invert good/bad for this benchmark
                </b-switch>
              </b-field>
              <p class="section-help">
                {{ directionHelp }}
              </p>
            </template>
          </ConditionalFormatEditor>

          <p
            v-if="thresholdsVaryByMeasure"
            class="section-help is-warning">
            Each measure decides whether its change is a difference or a
            percentage, so a threshold here is read in different units on
            different cards. Set the comparison type explicitly to fix what
            the thresholds mean.
          </p>

          <p
            v-if="!hasMeasureDirection"
            class="section-help">
            No direction is set on the KPI measures, so each benchmark still
            carries its own. Set one per measure to colour every benchmark
            from one place.
          </p>
        </div>
      </div>

      <div
        id="format"
        class="editor-section">
        <h2>Format</h2>
        <div class="body">
          <DataFormatEditor
            v-model="inputs.data_format"
            :errors="childErrors('data_format', errors)"
            :measure="inputs.measure_config?.measure"
            :type-override="formatTypeOverride" />
          <p class="section-help">
            {{ formatHelp }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Tabs from '@/components/pdf_report/Tabs.vue'
  import EditorSectionsMixin from '@/components/pdf_report/EditorSectionsMixin'
  import MeasureLabelInput from '@/components/pdf_report/MeasureLabelInput.vue'
  import MeasureConfigEditor from '@/components/reports/config/modules/common/MeasureConfigEditor'
  import ConditionalFormatEditor from '@/components/reports/config/modules/common/ConditionalFormatEditor'
  import DataFormatEditor from '@/components/reports/config/modules/common/DataFormatEditor'
  import MeasureMappingSelect from '@/components/pdf_report/kpi_module/MeasureMappingSelect.vue'
  import singleselect from '@/components/ui/singleselect.vue'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'

  export default {
    name: 'KpiBenchmark',
    components: {
      Tabs,
      MeasureLabelInput,
      MeasureConfigEditor,
      ConditionalFormatEditor,
      DataFormatEditor,
      MeasureMappingSelect,
      singleselect,
    },
    mixins: [EditorSectionsMixin,],
    props: {
      value: {
        type: Object,
        default: () => {},
      },
      errors: {
        type: Array,
        default: () => [],
      },
      context: {
        type: Object,
        default: () => {},
      },
    },
    data () {
      return {
        moduleConfigs: {
          name: 'Benchmark',
          type: 'KpiBenchmark',
          icon: require('@/assets/icons/kpi-row.svg'),
        },
        inputSectionTab: 'display-label',

        showOptions: [
          {
            key: 'both',
            label: 'The benchmark value and the change against it',
          },
          {
            key: 'value',
            label: 'The benchmark value only',
          },
          {
            key: 'var',
            label: 'The change only',
          },
        ],

        differenceTypeOptions: [
          {
            key: 'measure',
            label: 'Difference, as each measure defines it',
          },
          {
            key: 'diff',
            label: 'Difference to the benchmark',
          },
          {
            key: 'var',
            label: 'Percentage change from the benchmark',
          },
        ],

        directionLabels: {
          higher_is_better: 'higher is better',
          lower_is_better: 'lower is better',
          neutral: 'neutral, never coloured',
        },
      }
    },
    computed: {
      inputs: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },
      showsChange () {
        return this.inputs.show !== 'value'
      },
      showHelp () {
        const stacking = ' Each benchmark takes one line, stacked in the'
          + ' order they are listed.'
        if (this.inputs.show === 'value') {
          return 'The benchmark on its own — what it reads, with no'
            + ' comparison.' + stacking
        }
        if (this.inputs.show === 'var') {
          return 'The change on its own. The benchmark it is measured against'
            + ' is not shown, so the label has to name it.' + stacking
        }
        return 'The figure the benchmark reads, then the change against it,'
          + ' under its label.' + stacking
      },
      // Direction only moves to the measure once at least one measure sets
      // it; until then the benchmark keeps the control the row had, so a
      // half-configured module never silently loses its colouring.
      hasMeasureDirection () {
        return (this.context.kpiMeasures || []).some(
          (kpiMeasure) => !!kpiMeasure.good_direction
        )
      },
      directionHelp () {
        const described = (this.context.kpiMeasures || [])
          .filter((kpiMeasure) => !!kpiMeasure.good_direction)
          .map((kpiMeasure) => `${kpiMeasure.name || 'Untitled'} — ${
            this.directionLabels[kpiMeasure.good_direction]
          }`)
        if (!described.length) return ''
        const base = this.inputs.invert_direction
          ? 'This benchmark is coloured against each measure, inverted'
          : 'This benchmark is coloured in each measure\'s own direction'
        return `${base}: ${described.join(', ')}.`
      },
      derivesDifferenceFromMeasure () {
        return this.inputs.difference_type === 'measure'
      },
      thresholdsVaryByMeasure () {
        return this.derivesDifferenceFromMeasure &&
          !!this.inputs.conditional_format &&
          !this.inputs.conditional_format.comparison_type
      },
      inheritedComparisonHelp () {
        return this.derivesDifferenceFromMeasure
          ? 'falling back to a difference where the measure names none'
          : 'matching how the change is displayed'
      },
      inheritMeasureOption () {
        // Selecting this clears the measure, which is what makes the
        // benchmark a template: it takes whichever measure the card it lands
        // on carries.
        return { name: 'Use the KPI measure', }
      },
      formatTypeOverride () {
        if (this.inputs.measure_config?.is_pct_total) return 'percentage'
        // Only when the change is the single line drawn: with both lines the
        // format covers the value too, which is not a percentage.
        if (this.inputs.show === 'var' && this.inputs.difference_type === 'var') {
          return 'percentage'
        }
        return null
      },
      formatHelp () {
        const base = this.derivesDifferenceFromMeasure
          ? 'Leave unset and each measure picks its own — a percentage where'
            + ' it reads as a percentage change, its own format where it reads'
            + ' as a difference.'
          : 'Leave unset to use the KPI measure\'s format.'
        if (this.inputs.show !== 'both') return base
        return `${base} A format set here applies to both figures, so leaving`
          + ' it unset is usually what you want on a benchmark including both.'
      },
      tabs () {
        const tabs = [
          {
            label: 'Display label',
            id: 'display-label',
            hasErrors: false,
          },
          {
            label: 'Measure',
            id: 'measure',
            hasErrors: _childErrors('measure_config', this.errors).length > 0,
          },
          {
            label: 'Include',
            id: 'include',
            hasErrors: false,
          },
        ]
        if (this.showsChange) {
          tabs.push({
            label: 'Variance',
            id: 'variance',
            hasErrors: _childErrors('conditional_format', this.errors).length > 0,
          })
        }
        tabs.push({
          label: 'Format',
          id: 'format',
          hasErrors: _childErrors('data_format', this.errors).length > 0,
        })
        return tabs
      },
    },
    methods: {
      childErrors: _childErrors,
    },
  }
</script>

<style lang="scss" scoped>
@import '@/components/pdf_report/kpi_module/kpi-editor-panel.scss';

.section-help.is-warning {
  color: $warning;
}
</style>
